<template>

  <MainSiteLayout v-bind="{ content }">

    <BasicHero :content="{ title: content.title, subtitle: content.subtitle, breadcrumbs }" />

    <IndexPageLayout>

      <PaginatedProducts
        :products="content.products"
        :selectedCategory="selectedCategoryObj.value"
        :defaultImage="content.defaultImage"
      />

      <template v-slot:sidebar>
        <IndexPageSidebarMenu
          :heading="content.categoriesHeading"
          :categories="content.categories"
          :selectedCategoryObj="selectedCategoryObj"
        />
        <SidebarCtaButton v-if="content.sidebarCta" v-bind="content.sidebarCta" class="ctaButton" />
      </template>

      <template v-slot:bottom>
        <ContactPane v-if="content.contactPane" :content="content.contactPane" class="contactPane" />
      </template>

    </IndexPageLayout>

    <ProductDialog v-if="dialogContent" :content="dialogContent" />

  </MainSiteLayout>

</template>

<script>
const resourceGroupQueryFragment = `
  {
    _id,
    'slug': slug.current,
    title,
    resources[]{
      _type == 'download' => {
        _key,
        _type,
        title,
        url,
        systemRequirements,
      },
      _type == 'guide' => {
        _key,
        _type,
        'title': @->title,
        'slug': @->slug.current,
        'landingSectionId': @->landingSection._ref,
      },
    }
  }
`
export const contentQuery = `
  *[_id == $pageId][0]{
    locale,
    title,
    subtitle,
    categoriesHeading,
    categories[]->{ title, "slug": slug.current },
    sidebarCta,
    defaultImage,
    contactPane->,
    "products": *[_type == 'supportProduct' && locale == ^.locale && !(defined(isEmbargoed) && isEmbargoed)]{
      _type,
      locale,
      'slug': slug.current,
      priority,
      image,
      title,
      "pathComponent": slug.current,
      "category": category->slug.current,
      primaryResourceGroup->${resourceGroupQueryFragment},
      otherResourceGroups[]->${resourceGroupQueryFragment},
      "primaryProductGuide": articles[0]{ pathComponent },
    } | order(priority desc),
  }
`
</script>

<script setup>
import { onMounted, provide, reactive, ref } from 'vue'

import bodyScroll from '~/lib/bodyScroll.js'

import MainSiteLayout from '../layouts/MainSiteLayout.vue'

import BasicHero from './shared/BasicHero.vue'
import ContactPane from './shared/ContactPane.vue'
import IndexPageSidebarMenu from './index-page/IndexPageSidebarMenu.vue'
import PaginatedProducts from './index-page/PaginatedProducts.vue'
import ProductDialog from './index-page/ProductDialog.vue'
import SidebarCtaButton from './index-page/SidebarCtaButton.vue'
import IndexPageLayout from './index-page/IndexPageLayout.vue'

const dialogContent = ref(null)

const selectedCategoryObj = reactive({ value: null })

provide('showProductModal', function (content) {
  bodyScroll.lock()
  dialogContent.value = content
})

provide('hideProductModal', function () {
  bodyScroll.unlock()
  dialogContent.value = null
})

const { breadcrumbs, content, context } = defineProps({
  breadcrumbs: Array,
  content: Object,
  context: Object,
})

provide('locale', content.locale)
provide('context', context)

onMounted(
  function () {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search)
      selectedCategoryObj.value = params.get('category')
      const productName = params.get('product')
      if (productName) {
        const product = content.products.find(({ pathComponent }) => pathComponent === productName)
        if (product) {
          bodyScroll.lock()
          dialogContent.value = product
        }
      }
    }
  }
)
</script>


<style scoped>

.contactPane {
  margin-top: var(--spacing-17);
}

.ctaButton {
  margin-top: var(--spacing-8);
}
</style>
